import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Palette } from 'lucide-react';
import { CREATE_MY_BRAND_KIT, GET_MY_BRAND_KITS } from '@/queries/queries';
import { editorDataToKitData } from '@/lib/brand-kit/editorDataToKitData';
import type { EditorSubtitleState, EditorLogoState } from '@/lib/brand-kit/editorDataToKitData';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import type { LayoutFieldBinds } from '@/lib/layouts/layoutFieldBinds';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subtitle: EditorSubtitleState;
  logo: EditorLogoState;
  fontId: string | null;
  postLayout: PostLayoutConfig | null;
  fieldBinds?: LayoutFieldBinds;
  /** Called with the created kit id. Free users create but cannot apply —
   *  the caller decides whether to select it. */
  onCreated: (kitId: string) => void;
}

/** Save the editor's current subtitle/logo/layout look as a brand kit,
 *  named inline — no detour through /brand-kits. Creation is NOT PRO-gated
 *  (applying a kit is; free users keep their kits as saved presets). */
export const SaveBrandKitDialog: React.FC<Props> = ({
  open, onOpenChange, subtitle, logo, fontId, postLayout, fieldBinds, onCreated,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [createKit] = useMutation(CREATE_MY_BRAND_KIT, {
    // Write the created kit straight into the cached list: Apollo never
    // auto-appends to list queries, so without this "Meus Brand Kits" only
    // updated when the refetch round-trip happened to land. The refetch stays
    // as the authoritative refresh.
    update(cache, { data }) {
      const created = data?.createMyBrandKit;
      if (!created) return;
      const existing = cache.readQuery<{ myBrandKits: unknown[] }>({ query: GET_MY_BRAND_KITS });
      if (existing?.myBrandKits && !existing.myBrandKits.some((k) => (k as { id: string }).id === created.id)) {
        cache.writeQuery({
          query: GET_MY_BRAND_KITS,
          data: { myBrandKits: [...existing.myBrandKits, created] },
        });
      }
    },
    refetchQueries: [{ query: GET_MY_BRAND_KITS }],
    awaitRefetchQueries: true,
  });

  const save = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      const { data } = await createKit({
        variables: {
          input: {
            name: name.trim(),
            data: editorDataToKitData({ subtitle, logo, fontId, postLayout, fieldBinds }),
          },
        },
      });
      const id = data?.createMyBrandKit?.id;
      if (id) onCreated(id);
      setName('');
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to create brand kit from editor', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="save-brand-kit-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-primary" />
            {t('projectEditor.saveBrandKit.title')}
          </DialogTitle>
          <DialogDescription>{t('projectEditor.saveBrandKit.body')}</DialogDescription>
        </DialogHeader>
        <Input
          data-testid="save-brand-kit-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('projectEditor.saveBrandKit.namePlaceholder')}
          onKeyDown={(e) => { if (e.key === 'Enter') void save(); }}
        />
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="save-brand-kit-confirm"
            variant="gradient"
            disabled={!name.trim() || saving}
            onClick={() => void save()}
          >
            {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            {t('projectEditor.saveBrandKit.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveBrandKitDialog;
